import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Segment, Form, Button, Table, Icon, Message, Confirm} from 'semantic-ui-react';
import {FilePond, registerPlugin} from 'react-filepond';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileRename from 'filepond-plugin-file-rename';
import {Actions} from './../../../../../common/actions';

registerPlugin(
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginFileRename
);

const {
  saveFormAndCreateDoc,
  getWtsCourseDocs,
  deleteWtsCourseDoc,
  getWtsCourseDocFile,
  clearWtsCourseDocs,
} = Actions;

const WtsDocumentacionFinal = ({courseToEdit}) => {
  const dispatch = useDispatch();
  const processedDocRef = useRef(null);

  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const user = useSelector((state) => state.auth?.user ||
    state.session?.session?.user || state.session?.user || 'ADMIN');

  const {docsRaw, loading, currentDocFile} = useSelector((state) => ({
    'docsRaw': state.wtsCourseDocs?.list || state.wtsCourseDocuments?.courseDocuments || [],
    'loading': state.wtsCourseDocs?.loading || state.wtsCourseDocuments?.loading || false,
    'currentDocFile': state.wtsCourseDocs?.currentDocFile || state.wtsCourseDocuments?.currentDocFile,
  }));

  const docsList = Array.isArray(docsRaw) ? docsRaw : (docsRaw?.data || []);

  useEffect(() => {
    const courseId = courseToEdit?.course_id || courseToEdit?.id;
    if (courseId) {
      dispatch(getWtsCourseDocs(courseId));
    }
    return () => {
      if (typeof clearWtsCourseDocs === 'function') {
        dispatch(clearWtsCourseDocs());
      }
    };
  }, [courseToEdit, dispatch]);

  useEffect(() => {
    if (
      currentDocFile?.answers?.fileBase64 &&
      currentDocFile?._id !== processedDocRef.current
    ) {
      processedDocRef.current = currentDocFile?._id;
      const byteChars = atob(currentDocFile.answers.fileBase64);
      const byteArray = new Uint8Array([...byteChars].map((c) => c.charCodeAt(0)));
      const blob = new Blob([byteArray], {'type': 'application/pdf'});
      window.open(URL.createObjectURL(blob));
    }
  }, [currentDocFile]);

  const finalDocs = docsList.filter((doc) => {
    const docType = doc.doc_type || doc.tipo_documento || doc.type;
    return docType === 'FINAL_DOC' || docType === 'OTHER';
  });

  const handleUpload = async(e) => {
    e.preventDefault();
    e.stopPropagation();
    const courseId = courseToEdit?.course_id || courseToEdit?.id;
    
    if (!file || !courseId) {
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async() => {
      try {
        const base64Content = reader.result.split(',')[1];
        const formData = new FormData();
        formData.append('targetCollection', 'wtsDocStorage');
        formData.append('type', 'WTS_DOC_STORAGE');
        formData.append('status', 'published');
        formData.append('generalInfo', JSON.stringify({
          'courseId': courseId,
          'category': 'FINAL_DOC',
          'fileName': file.name,
          'uploadDate': new Date().toISOString(),
        }));
        formData.append('answers', JSON.stringify({
          'fileBase64': base64Content,
        }));
        
        if (typeof saveFormAndCreateDoc === 'function') {
          await dispatch(saveFormAndCreateDoc(formData, courseId, 'FINAL_DOC', file.name, user));
          setTimeout(() => {
            dispatch(getWtsCourseDocs(courseId));
          }, 500);
        }
        
        setFile(null);
        setShowAdd(false);
      } catch (error) {
        console.error(error);
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const confirmDelete = (doc) => {
    setDocToDelete(doc);
    setConfirmOpen(true);
  };

  const handleDelete = async() => {
    if (!docToDelete) {
      return;
    }
    try {
      const courseId = courseToEdit?.course_id || courseToEdit?.id;
      const mongoId = docToDelete.mongo_id || docToDelete.id_mongo;
      const docId = docToDelete.id || docToDelete.docId;

      if (typeof deleteWtsCourseDoc === 'function') {
        await dispatch(deleteWtsCourseDoc(courseId, docId, mongoId));
        dispatch(getWtsCourseDocs(courseId));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmOpen(false);
      setDocToDelete(null);
    }
  };

  const handleDownload = (doc) => {
    processedDocRef.current = null;
    if (typeof getWtsCourseDocFile === 'function') {
      const mongoId = doc.mongo_id || doc.id_mongo;
      dispatch(getWtsCourseDocFile(mongoId));
    }
  };

  return (
    <>
      <Segment
        padded="very"
        loading={loading || isUploading}
        style={{
          'minHeight': '400px', 'display': 'flex', 'flexDirection': 'column',
          'border': 'none', 'boxShadow': 'none', 'padding': '0',
        }}
      >
        <div
          style={{
            'backgroundColor': '#ffffff',
            'padding': '15px 20px',
            'borderRadius': '8px',
            'border': '1px solid #e0e0e0',
            'marginBottom': '20px',
            'boxShadow': '0 2px 4px rgba(0,0,0,0.02)',
            'display': 'flex',
            'justifyContent': 'space-between',
            'alignItems': 'center',
          }}
        >
          <div style={{'color': '#333', 'fontWeight': 'bold', 'fontSize': '1.1em'}}>
            <Icon name="file pdf outline" style={{'color': '#db2828'}} /> Documentación Final
          </div>
          <Button
            type="button"
            basic
            circular
            compact
            inverted
            className="p-0"
            onClick={(e) => {
              e.preventDefault();
              setShowAdd(!showAdd);
            }}
            icon={
              <Icon
                link
                circular
                inverted
                size="small"
                name={showAdd ? 'close' : 'add'}
                color={showAdd ? 'red' : 'green'}
              />
            }
          />
        </div>

        {showAdd && (
          <Segment style={{'backgroundColor': '#f9fafb', 'marginBottom': '20px', 'boxShadow': 'none'}}>
            <Form>
              <Form.Field>
                <label>Archivo PDF</label>
                <FilePond
                  maxFileSize="10MB"
                  maxTotalFileSize="10MB"
                  acceptedFileTypes={['application/pdf']}
                  credits={false}
                  onupdatefiles={(fileItems) => {
                    if (fileItems.length > 0) {
                      setFile(fileItems[0].file);
                    } else {
                      setFile(null);
                    }
                  }}
                  allowMultiple={false}
                  labelIdle='Arrastrar PDF o <span class="filepond--label-action">Navegar</span>'
                />
              </Form.Field>
              <div style={{'textAlign': 'right'}}>
                <Button
                  color="blue"
                  icon="upload"
                  content="Subir Documento"
                  labelPosition="left"
                  onClick={handleUpload}
                  loading={isUploading}
                  disabled={!file || isUploading}
                />
              </div>
            </Form>
          </Segment>
        )}

        <div style={{'flex': 1, 'overflowY': 'auto', 'overflowX': 'hidden'}}>
          {finalDocs.length > 0 ? (
            <Table celled compact="very" size="small" textAlign="center">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={1}>Nro</Table.HeaderCell>
                  <Table.HeaderCell width={12}>Nombre del Archivo</Table.HeaderCell>
                  <Table.HeaderCell width={3}>Acciones</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {finalDocs.map((doc, index) => (
                  <Table.Row key={doc.id || doc._id || doc.mongo_id || index}>
                    <Table.Cell>{index + 1}</Table.Cell>
                    <Table.Cell textAlign="left">
                      <Icon name="pdf file outline" color="red" />
                      <a
                        href="#!"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownload(doc);
                        }}
                        style={{'cursor': 'pointer', 'color': '#4183c4'}}
                      >
                        {doc.file_name || doc.nombre_archivo || 'Documento PDF'}
                      </a>
                    </Table.Cell>
                    <Table.Cell>
                      <Button.Group size="small">
                        <Button
                          icon="trash alternate"
                          color="red"
                          basic
                          onClick={() => confirmDelete(doc)}
                          title="Eliminar"
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <Message
              info
              icon="info circle"
              content="No hay documentos finales cargados para este curso."
            />
          )}
        </div>
      </Segment>

      <Confirm
        open={confirmOpen}
        header="Eliminar Documento"
        content="¿Está seguro que desea eliminar este documento final? Esta acción no se puede deshacer."
        cancelButton={{'content': 'Cancelar', 'color': 'grey'}}
        confirmButton={{'content': 'Sí, eliminar', 'color': 'red'}}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default WtsDocumentacionFinal;
